import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Chip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Select,
  SelectItem,
  Spinner,
} from '@heroui/react';
import { ArrowLeft, Mail, CheckCircle, XCircle } from 'lucide-react';
import { useUnclassifiedEmails, useClassifyEmail, useDiscardEmail } from '@/api/unclassified';
import { useProjects } from '@/api/projects';
import type { UnclassifiedEmail } from '@estimator/shared';

export default function UnclassifiedEmailsPage() {
  const navigate = useNavigate();
  const { data: emailsResponse, isLoading, isError } = useUnclassifiedEmails();
  const emails = emailsResponse?.data ?? [];
  const { data: projectsData } = useProjects();
  const classifyMutation = useClassifyEmail();
  const discardMutation = useDiscardEmail();

  const [classifyTarget, setClassifyTarget] = useState<UnclassifiedEmail | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [discardTarget, setDiscardTarget] = useState<UnclassifiedEmail | null>(null);

  const projects = projectsData?.data ?? [];

  const handleClassify = () => {
    if (!classifyTarget || !selectedProjectId) return;
    classifyMutation.mutate(
      { id: classifyTarget.id, project_id: selectedProjectId },
      {
        onSuccess: () => {
          setClassifyTarget(null);
          setSelectedProjectId('');
        },
      },
    );
  };

  const handleDiscard = () => {
    if (!discardTarget) return;
    discardMutation.mutate(discardTarget.id, {
      onSuccess: () => setDiscardTarget(null),
    });
  };

  return (
    <div className="min-h-full" style={{ backgroundColor: 'var(--bg-base)' }}>
      {/* Header */}
      <div
        className="border-b px-6 py-4"
        style={{
          borderColor: 'var(--border)',
          backgroundColor: 'var(--bg-surface)',
        }}
      >
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 text-xs transition-colors focus:outline-none"
            style={{ color: 'var(--text-muted)' }}
          >
            <ArrowLeft size={12} />
            Dashboard
          </button>
        </div>
        <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
          Unclassified Emails
        </h1>
        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
          Emails that could not be automatically assigned to a project
        </p>
      </div>

      {/* Content */}
      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <Spinner size="md" color="primary" />
          </div>
        ) : isError ? (
          <div className="flex items-center justify-center py-24">
            <p className="text-sm" style={{ color: 'var(--red)' }}>
              Failed to load unclassified emails.
            </p>
          </div>
        ) : emails.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center py-24 gap-4 rounded-xl border"
            style={{ borderColor: 'var(--border)', borderStyle: 'dashed' }}
          >
            <Mail size={48} style={{ color: 'var(--text-muted)' }} />
            <div className="text-center">
              <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                No unclassified emails
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                All incoming emails have been assigned to projects
              </p>
            </div>
          </div>
        ) : (
          <div
            className="rounded-xl border overflow-hidden"
            style={{
              backgroundColor: 'var(--bg-surface)',
              borderColor: 'var(--border)',
            }}
          >
            {/* Table header */}
            <div
              className="grid grid-cols-[1fr_150px_120px_150px_80px_120px] gap-3 px-4 py-2 border-b text-[10px] font-ui uppercase tracking-wide"
              style={{
                borderColor: 'var(--border)',
                color: 'var(--text-muted)',
                backgroundColor: 'var(--bg-panel)',
              }}
            >
              <span>Subject</span>
              <span>Sender</span>
              <span>Received</span>
              <span>Suggested Project</span>
              <span>Confidence</span>
              <span>Actions</span>
            </div>

            {/* Rows */}
            {emails.map((email) => (
              <div
                key={email.id}
                className="grid grid-cols-[1fr_150px_120px_150px_80px_120px] gap-3 px-4 py-3 border-b items-center"
                style={{ borderColor: 'var(--border)' }}
              >
                <span
                  className="text-xs font-medium truncate"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {email.subject ?? '(No subject)'}
                </span>
                <span className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>
                  {email.sender?.name ?? email.sender?.email ?? 'Unknown'}
                </span>
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {email.received_at
                    ? new Date(email.received_at).toLocaleDateString()
                    : '--'}
                </span>
                <span className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>
                  {email.ai_suggested_project_id
                    ? projects.find((p) => p.id === email.ai_suggested_project_id)
                        ?.project_name ?? 'Unknown'
                    : '--'}
                </span>
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {email.ai_confidence != null
                    ? `${Math.round(email.ai_confidence * 100)}%`
                    : '--'}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="flat"
                    color="success"
                    className="min-w-0 h-7 px-2 text-[10px]"
                    startContent={<CheckCircle size={12} />}
                    onPress={() => setClassifyTarget(email)}
                  >
                    Classify
                  </Button>
                  <Button
                    size="sm"
                    variant="flat"
                    color="danger"
                    className="min-w-0 h-7 px-2 text-[10px]"
                    isIconOnly
                    onPress={() => setDiscardTarget(email)}
                  >
                    <XCircle size={12} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Classify Modal */}
      <Modal isOpen={!!classifyTarget} onClose={() => setClassifyTarget(null)} size="sm">
        <ModalContent style={{ backgroundColor: 'var(--bg-surface)' }}>
          <ModalHeader
            className="text-sm font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Classify Email
          </ModalHeader>
          <ModalBody>
            <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
              Assign &ldquo;{classifyTarget?.subject}&rdquo; to a project:
            </p>
            <Select
              size="sm"
              label="Project"
              aria-label="Select project"
              selectedKeys={selectedProjectId ? [selectedProjectId] : []}
              onSelectionChange={(keys) => {
                const key = Array.from(keys)[0] as string;
                if (key) setSelectedProjectId(key);
              }}
              classNames={{
                trigger: 'bg-[var(--bg-panel)] border-[var(--border)]',
                value: 'text-xs text-[var(--text-primary)]',
                popoverContent: 'bg-[var(--bg-panel)] border border-[var(--border)]',
              }}
            >
              {projects.map((p) => (
                <SelectItem key={p.id} textValue={p.project_name}>
                  <span className="text-xs" style={{ color: 'var(--text-primary)' }}>
                    {p.project_name}
                  </span>
                </SelectItem>
              ))}
            </Select>
          </ModalBody>
          <ModalFooter>
            <Button
              size="sm"
              variant="bordered"
              onPress={() => setClassifyTarget(null)}
              className="border-[var(--border)] text-[var(--text-secondary)]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              color="primary"
              onPress={handleClassify}
              isDisabled={!selectedProjectId}
              isLoading={classifyMutation.isPending}
            >
              Classify
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Discard Confirmation Modal */}
      <Modal isOpen={!!discardTarget} onClose={() => setDiscardTarget(null)} size="sm">
        <ModalContent style={{ backgroundColor: 'var(--bg-surface)' }}>
          <ModalHeader
            className="text-sm font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Discard Email
          </ModalHeader>
          <ModalBody>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              Are you sure you want to discard &ldquo;{discardTarget?.subject}&rdquo;?
              This email will not be assigned to any project.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              size="sm"
              variant="bordered"
              onPress={() => setDiscardTarget(null)}
              className="border-[var(--border)] text-[var(--text-secondary)]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              color="danger"
              onPress={handleDiscard}
              isLoading={discardMutation.isPending}
            >
              Discard
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
